import React, { useState } from 'react';
import { X, Phone, MessageSquare, CheckCircle2, AlertCircle } from 'lucide-react';
import Button from './Button';
import { contactData } from '../data/contactData';

export default function ConsultationModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    patientName: '',
    contactPerson: '',
    phone: '',
    email: '',
    service: 'Mental Healthcare',
    consultationType: 'Inpatient Assessment',
    preferredDate: '',
    notes: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch('https://formsubmit.co/ajax/contact@umedrehabilitation.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: 'New Psychiatric Consultation Request - UMED Rehabilitation Centre',
          _template: 'table',
          _captcha: 'false',
          'Patient / Individual Name': formData.patientName,
          'Relative / Caregiver Name': formData.contactPerson || 'Self / Not Specified',
          'Contact Phone Number': formData.phone,
          'Email Address': formData.email || 'Not Provided',
          'Area of Care Needed': formData.service,
          'Consultation Type': formData.consultationType,
          'Brief Context / Symptoms': formData.notes || 'No symptoms provided',
          'Submitted Date & Time': new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
        })
      });
    } catch (err) {
      console.error('FormSubmit submission error:', err);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-[#123B5D]/60 backdrop-blur-sm transition-all duration-300">
      <div 
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-elevated border border-[#D9E8E5] overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="bg-[#123B5D] text-white p-6 sm:p-8 relative">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full text-[#D9E8E5] hover:text-white hover:bg-[#0F766E] transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <span className="h-2 w-2 rounded-full bg-[#2A9D8F]"></span>
            <span className="text-xs uppercase tracking-widest text-[#2A9D8F] font-semibold">Strictly Confidential</span>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl text-white font-normal">
            Request a Psychiatric Consultation
          </h3>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 max-h-[75vh] overflow-y-auto">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#F5FAF9] text-[#0F766E] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#D9E8E5]">
                <CheckCircle2 className="w-8 h-8 text-[#0F766E]" />
              </div>
              <h4 className="font-serif text-2xl text-[#123B5D] mb-2">Consultation Request Recorded</h4>
              <p className="text-[#64748B] text-sm max-w-md mx-auto mb-6 leading-relaxed">
                Thank you for reaching out. In an operational deployment, our psychiatric coordinator will contact you promptly at <strong className="text-[#123B5D]">{formData.phone || 'your provided number'}</strong>.
              </p>
              
              <div className="p-4 bg-[#F5FAF9] rounded-xl border border-[#D9E8E5] text-left text-xs text-[#18323A] mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#0F766E] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#123B5D] mb-1">Immediate Assistance Required?</p>
                  <p>If you or your loved one is in acute distress, call our round-the-clock emergency line directly.</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button href={`tel:${contactData.phones[0].raw}`} variant="primary" size="md">
                  <Phone className="w-4 h-4 mr-2" /> Call 24/7 Helpline
                </Button>
                <Button href={contactData.whatsapp.link} variant="secondary" size="md">
                  <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp Care Desk
                </Button>
                <Button onClick={handleReset} variant="outline" size="md">
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#18323A] uppercase tracking-wider mb-1.5">
                    Patient / Individual Name *
                  </label>
                  <input
                    type="text"
                    required
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D9E8E5] text-sm text-[#18323A] focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#18323A] uppercase tracking-wider mb-1.5">
                    Your Name (If relative / caregiver)
                  </label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    placeholder="e.g. Brother, Mother, Self"
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D9E8E5] text-sm text-[#18323A] focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#18323A] uppercase tracking-wider mb-1.5">
                    Contact Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98000 00000"
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D9E8E5] text-sm text-[#18323A] focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#18323A] uppercase tracking-wider mb-1.5">
                    Email Address (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D9E8E5] text-sm text-[#18323A] focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#18323A] uppercase tracking-wider mb-1.5">
                    Area of Care Needed
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D9E8E5] text-sm text-[#18323A] focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-white"
                  >
                    <option value="Mental Healthcare">Comprehensive Mental Healthcare</option>
                    <option value="Deaddiction & Recovery">Deaddiction & Detoxification</option>
                    <option value="Psychiatric Rehabilitation">Psychiatric Rehabilitation</option>
                    <option value="Counselling & Psychotherapy">Counselling & Psychotherapy</option>
                    <option value="Family Guidance">Family Support & Guidance</option>
                    <option value="General Psychiatric Evaluation">General Psychiatric Evaluation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#18323A] uppercase tracking-wider mb-1.5">
                    Consultation Type
                  </label>
                  <select
                    name="consultationType"
                    value={formData.consultationType}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-[#D9E8E5] text-sm text-[#18323A] focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition bg-white"
                  >
                    <option value="Inpatient Assessment">Inpatient Admission Assessment</option>
                    <option value="Outpatient OPD">Outpatient OPD Consultation</option>
                    <option value="Virtual / Tele-Consultation">Virtual / Tele-Consultation</option>
                    <option value="Emergency Crisis Guidance">Emergency Crisis Guidance</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#18323A] uppercase tracking-wider mb-1.5">
                  Brief Context / Symptoms (Confidential)
                </label>
                <textarea
                  name="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Share any key symptoms, duration, or specific concerns to help our team prepare..."
                  className="w-full px-4 py-2.5 rounded-lg border border-[#D9E8E5] text-sm text-[#18323A] focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] outline-none transition resize-none"
                ></textarea>
              </div>

              <div className="pt-2 flex items-center justify-end gap-3">
                <Button type="button" onClick={onClose} variant="outline" size="sm">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" size="md" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
